import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { ErroAPI, api, data, dataHora, desde } from "../api.ts";
import { Cartao, Medidor, Selo, SeloStatus, Vazio } from "../componentes/basicos.tsx";
import { useSessao, ehAdmin, podeEscrever } from "../sessao.tsx";
import { useDados } from "../usarDados.ts";

type Resposta = {
  licenca: {
    id: number; empresa_id: number; empresa: string; empresa_cnpj: string;
    apelido: string; status: string; motivo_status: string; validade: string | null;
    max_instancias: number; max_maquinas: number; modo_offline: string;
    limite_produto: Record<string, number>;
    tolerancia_offline_h: number; intervalo_heartbeat_s: number;
    maquinas_ativas: number;
  };
  maquinas: {
    id: number; hostname: string; fingerprint: string; status: string;
    ultimo_contato: string | null; versao_app: string; ultimo_erro: string;
  }[];
  codigos: {
    codigo: string; formatado: string; criado_em: string; expira_em: string;
    usos: number; usos_max: number; cancelado_em: string | null;
  }[];
  tokens: {
    jti: string; kid: string; motivo_emissao: string; emitido_em: string;
    expira_em: string; revogado_em: string | null; hostname: string;
  }[];
  auditoria: { id: number; ocorrido_em: string; ator: string; acao: string }[];
  pico: {
    picoJanela: number; picoHistorico: number; diaDoPico: string | null;
    /** Vazio ate o Automatiza reportar o produto de cada instancia. */
    porProduto: Record<string, number>;
  };
};

const curto = (fpr: string) => fpr.replace(/^sha256:/, "").slice(0, 8);

/** Produtos do Automatiza, na ordem em que o app os declara (app/catalog.py). */
const PRODUTOS = ["automatiza", "veiculos"] as const;
const NOME_PRODUTO: Record<string, string> = {
  automatiza: "Automatiza", veiculos: "Veiculos",
};

export function Licenca() {
  const { id } = useParams();
  const { usuario } = useSessao();
  const { dados: r, erro, carregando, recarregar } = useDados<Resposta>(`/licencas/${id}`);
  const [codigoNovo, setCodigoNovo] = useState("");
  const [tokenNovo, setTokenNovo] = useState("");
  const [aviso, setAviso] = useState("");
  const [falha, setFalha] = useState("");

  async function acao(fn: () => Promise<{ mensagem?: string }>) {
    setFalha(""); setAviso("");
    try {
      const r = await fn();
      if (r?.mensagem) setAviso(r.mensagem);
      recarregar();
    } catch (e) {
      setFalha(e instanceof ErroAPI ? e.message : "Nao consegui concluir.");
    }
  }

  if (carregando) return <div className="carregando">carregando...</div>;
  if (erro || !r) return <div className="alerta alerta--erro">{erro}</div>;
  const l = r.licenca;
  // Teto por produto so aparece no cartao de uso quando existe: medidor de um
  // limite que ninguem definiu seria barra cheia sem significado.
  const tetos = Object.entries(l.limite_produto ?? {}).filter(([, v]) => v > 0);

  return (
    <>
      <div className="entre" style={{ marginBottom: "var(--e-lg)" }}>
        <div>
          <h1 className="mb-0">{l.apelido || `Licenca ${l.id}`}</h1>
          <span className="suave pequeno">
            <Link to={`/empresas/${l.empresa_id}`}>{l.empresa}</Link> &middot; {l.empresa_cnpj}
          </span>
        </div>
        <div className="linha">
          {/* Selo de modo so quando e `bloquear`: `avisar` e o padrao, e um selo
              para o padrao seria ruido em toda tela. */}
          {l.modo_offline === "bloquear" && <Selo tom="critico">bloqueia sem contato</Selo>}
          <SeloStatus status={l.status} />
          <Link className="botao botao--secundario" to={`/empresas/${l.empresa_id}`}>Voltar</Link>
        </div>
      </div>

      {aviso && <div className="alerta alerta--sucesso">{aviso}</div>}
      {falha && <div className="alerta alerta--erro">{falha}</div>}
      {l.motivo_status && (
        <div className="alerta alerta--aviso">
          <span className="alerta__titulo">Motivo do status</span>{l.motivo_status}
        </div>
      )}

      <Cartao titulo="Uso contra o contrato">
        <div className="grade grade--2">
          <Medidor rotulo="Vagas de maquina" usado={l.maquinas_ativas}
                   total={l.max_maquinas} unidade="vagas" />
          <Medidor rotulo="Instancias" usado={r.pico.picoJanela}
                   total={l.max_instancias} unidade=""
                   nota="no pico dos ultimos 30 dias" />
        </div>
        {tetos.length > 0 && (
          <div className="grade grade--2 mt-lg">
            {tetos.map(([p, teto]) => (
              <Medidor key={p} rotulo={`Instancias de ${NOME_PRODUTO[p] ?? p}`}
                       usado={r.pico.porProduto?.[p] ?? 0} total={teto} unidade=""
                       nota="teto proprio deste produto" />
            ))}
          </div>
        )}
        {r.pico.picoHistorico > 0 && (
          <p className="pequeno suave mt-lg mb-0">
            Pico historico: {r.pico.picoHistorico} instancias
            {r.pico.diaDoPico && ` em ${data(r.pico.diaDoPico)}`}
          </p>
        )}
      </Cartao>

      {codigoNovo && (
        <Cartao titulo="Codigo de ativacao">
          <div className="codigo-destaque">{codigoNovo}</div>
          <p className="suave pequeno mb-0">
            Dite este codigo ao tecnico. Ele digita na aba Licenca do Automatiza e a
            aplicacao troca por uma licenca assinada, ja amarrada aquele computador.
            Vale por 7 dias e nao volta a ser exibido.
          </p>
        </Cartao>
      )}

      {tokenNovo && (
        <Cartao titulo="Token para instalacao sem internet">
          <textarea className="token-caixa" readOnly value={tokenNovo} />
          <div className="alerta alerta--aviso mt-lg">
            <span className="alerta__titulo">Atencao</span>
            Esta instalacao so podera ser revogada remotamente depois do primeiro
            contato com o servidor. Ate la, o unico jeito de parar e no proprio
            computador do cliente.
          </div>
        </Cartao>
      )}

      {podeEscrever(usuario) && (
        <Cartao titulo="Ativacao">
          <div className="linha">
            <button className="botao" type="button" onClick={() => void acao(async () => {
              const res = await api.post<{ codigo: string }>(`/licencas/${id}/codigo`, { usos: 1 });
              setCodigoNovo(res.codigo);
              return {};
            })}>Gerar codigo de ativacao</button>
          </div>

          {ehAdmin(usuario) && (
            <details className="mt-lg">
              <summary className="suave pequeno" style={{ cursor: "pointer" }}>
                Instalacao sem internet
              </summary>
              <p className="suave pequeno mt-lg">
                Peca ao tecnico a digital que aparece na aba Licenca do Automatiza e cole
                abaixo. O token gerado ja vem amarrado aquele computador.
              </p>
              <form onSubmit={(ev) => {
                ev.preventDefault();
                const fpr = new FormData(ev.currentTarget).get("fingerprint") as string;
                void acao(async () => {
                  const res = await api.post<{ token: string }>(`/licencas/${id}/emitir`, { fingerprint: fpr });
                  setTokenNovo(res.token);
                  return {};
                });
              }}>
                <label className="campo">
                  <span className="campo__rotulo">Digital do computador</span>
                  <input type="text" name="fingerprint" placeholder="sha256:..." className="mono" />
                </label>
                <button className="botao botao--secundario" type="submit">Emitir token</button>
              </form>
            </details>
          )}
        </Cartao>
      )}

      <Cartao titulo="Maquinas">
        {r.maquinas.length === 0 ? (
          <Vazio titulo="Nenhuma maquina ativada">
            Gere um codigo de ativacao acima e passe ao tecnico.
          </Vazio>
        ) : (
          <div className="tabela-rolagem">
            <table className="tabela">
              <thead>
                <tr>
                  <th>Computador</th><th>Status</th><th>Ultimo contato</th>
                  <th>Versao</th><th>Ultimo erro</th><th>Digital</th><th></th>
                </tr>
              </thead>
              <tbody>
                {r.maquinas.map((m) => (
                  <tr key={m.id}>
                    <td><Link to={`/maquinas/${m.id}`}>{m.hostname || "sem nome"}</Link></td>
                    <td><SeloStatus status={m.status} /></td>
                    <td className="suave pequeno">{desde(m.ultimo_contato)}</td>
                    <td className="suave pequeno">{m.versao_app}</td>
                    {/* A string do erro E o valor. Um ponto vermelho dizendo
                        "houve excecao" sem dizer qual e pior que nada. */}
                    <td className="tabela__erro" title={m.ultimo_erro}>{m.ultimo_erro}</td>
                    <td className="tabela__mono">{curto(m.fingerprint)}</td>
                    <td className="direita">
                      {podeEscrever(usuario) && m.status === "ativa" && (
                        <button className="botao botao--secundario botao--pequeno" type="button"
                                onClick={() => void acao(() =>
                                  api.post(`/maquinas/${m.id}/liberar`, { motivo: "troca de servidor" }))}>
                          Liberar vaga
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Cartao>

      {podeEscrever(usuario) && (
        <Cartao titulo="Limites e comportamento">
          <form onSubmit={(ev) => {
            ev.preventDefault();
            const f = new FormData(ev.currentTarget);
            void acao(() => api.put(`/licencas/${id}`, {
              apelido: f.get("apelido"),
              max_instancias: Number(f.get("max_instancias")),
              max_maquinas: Number(f.get("max_maquinas")),
              validade: f.get("validade") || null,
              modo_offline: f.get("modo_offline"),
              tolerancia_offline_h: Number(f.get("tolerancia_offline_h")),
              intervalo_heartbeat_s: Number(f.get("intervalo_heartbeat_s")),
              avisar_cliente: f.get("avisar_cliente") === "on",
              // Sempre os dois produtos, sempre: campo em branco vira ausencia
              // de teto no banco. Mandar so o preenchido impediria de REMOVER
              // um teto pela tela.
              limite_produto: Object.fromEntries(
                PRODUTOS.map((p) => [p, Number(f.get(`teto_${p}`)) || 0]),
              ),
            }));
          }}>
            <div className="grade grade--3">
              <label className="campo"><span className="campo__rotulo">Apelido</span>
                <input type="text" name="apelido" defaultValue={l.apelido} placeholder="Balanca Matriz" /></label>
              <label className="campo"><span className="campo__rotulo">Instancias simultaneas</span>
                <input type="number" name="max_instancias" defaultValue={l.max_instancias} min={1} max={64} />
                {/* Editar um limite sem ver o uso e como se vende o plano errado. */}
                <span className="campo__dica">hoje: {r.pico.picoJanela} no pico de 30 dias</span></label>
              <label className="campo"><span className="campo__rotulo">Maquinas</span>
                <input type="number" name="max_maquinas" defaultValue={l.max_maquinas} min={1} max={32} />
                <span className="campo__dica">hoje: {l.maquinas_ativas} ocupando vaga</span></label>
              <label className="campo"><span className="campo__rotulo">Validade</span>
                <input type="date" name="validade" defaultValue={l.validade?.slice(0, 10) ?? ""} />
                <span className="campo__dica">Vazio = sem vencimento.</span></label>
              <label className="campo"><span className="campo__rotulo">Sem contato com o servidor</span>
                <select name="modo_offline" defaultValue={l.modo_offline}>
                  <option value="avisar">Somente avisar</option>
                  <option value="bloquear">Bloquear apos a tolerancia</option>
                </select>
                <span className="campo__dica">Avisar e o padrao: rede ruim do cliente nao para a balanca.</span></label>
              <label className="campo"><span className="campo__rotulo">Tolerancia (horas)</span>
                <input type="number" name="tolerancia_offline_h" defaultValue={l.tolerancia_offline_h} min={1} max={8760} />
                <span className="campo__dica">336 = 14 dias.</span></label>
              <label className="campo"><span className="campo__rotulo">Heartbeat (segundos)</span>
                <input type="number" name="intervalo_heartbeat_s" defaultValue={l.intervalo_heartbeat_s} min={300} max={86400} />
                <span className="campo__dica">21600 = 6 horas.</span></label>
            </div>

            {/* O Automatiza abre instancias de dois produtos, com catalogos
                separados (app/catalog.py). O total acima vale para os dois
                somados; aqui se aperta um deles, quando o contrato aperta. */}
            <p className="suave pequeno mb-0" style={{ marginTop: "var(--e-lg)" }}>
              <strong>Teto por produto</strong> — opcional. Vazio = sem teto proprio,
              vale so o total de {l.max_instancias} acima.
            </p>
            <div className="grade grade--3">
              {PRODUTOS.map((p) => (
                <label className="campo" key={p}>
                  <span className="campo__rotulo">{NOME_PRODUTO[p]}</span>
                  <input type="number" name={`teto_${p}`} min={1} max={64}
                         placeholder="sem teto"
                         defaultValue={l.limite_produto?.[p] ?? ""} />
                  <span className="campo__dica">
                    {r.pico.porProduto?.[p]
                      ? `hoje: ${r.pico.porProduto[p]} no pico de 30 dias`
                      : "sem uso reportado"}
                  </span>
                </label>
              ))}
            </div>

            <button className="botao" type="submit">Salvar</button>
          </form>
        </Cartao>
      )}

      {ehAdmin(usuario) && (
        <Cartao titulo="Situacao da licenca">
          <div className="grade grade--2">
            {l.status === "ativa" ? (
              <>
                <form onSubmit={(ev) => {
                  ev.preventDefault();
                  const m = new FormData(ev.currentTarget).get("motivo") as string;
                  void acao(() => api.post(`/licencas/${id}/suspender`, { motivo: m }));
                }}>
                  <label className="campo"><span className="campo__rotulo">Suspender</span>
                    <input type="text" name="motivo" placeholder="Motivo" required /></label>
                  <button className="botao botao--secundario" type="submit">Suspender</button>
                  <span className="campo__dica">Reversivel na hora: os tokens continuam validos.</span>
                </form>
                <form onSubmit={(ev) => {
                  ev.preventDefault();
                  const m = new FormData(ev.currentTarget).get("motivo") as string;
                  void acao(() => api.post(`/licencas/${id}/revogar`, { motivo: m }));
                }}>
                  <label className="campo"><span className="campo__rotulo">Revogar</span>
                    <input type="text" name="motivo" placeholder="Motivo" required /></label>
                  <button className="botao botao--perigo" type="submit">Revogar</button>
                  <span className="campo__dica">Mata todos os tokens. Voltar exige codigo novo.</span>
                </form>
              </>
            ) : (
              <form onSubmit={(ev) => {
                ev.preventDefault();
                const m = new FormData(ev.currentTarget).get("motivo") as string;
                void acao(() => api.post(`/licencas/${id}/reativar`, { motivo: m }));
              }}>
                <label className="campo"><span className="campo__rotulo">Reativar</span>
                  <input type="text" name="motivo" placeholder="Motivo" /></label>
                <button className="botao" type="submit">Reativar licenca</button>
              </form>
            )}
          </div>
        </Cartao>
      )}

      <div className="grade grade--2">
        <Cartao titulo="Codigos de ativacao">
          {r.codigos.length === 0 ? <Vazio titulo="Nenhum codigo gerado" /> : (
            <table className="tabela">
              <thead><tr><th>Codigo</th><th>Vale ate</th><th>Usos</th><th>Situacao</th></tr></thead>
              <tbody>
                {r.codigos.map((c) => {
                  const expirado = new Date(c.expira_em) < new Date();
                  return (
                    <tr key={c.codigo}>
                      <td className="tabela__mono">{c.formatado}</td>
                      <td className="suave pequeno">{dataHora(c.expira_em)}</td>
                      <td className="tabela__num">{c.usos} / {c.usos_max}</td>
                      <td>
                        {c.cancelado_em ? <Selo tom="neutro">cancelado</Selo>
                          : c.usos >= c.usos_max ? <Selo tom="neutro">usado</Selo>
                          : expirado ? <Selo tom="critico">expirado</Selo>
                          : <Selo tom="ok">valido</Selo>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Cartao>

        <Cartao titulo="Tokens emitidos">
          {r.tokens.length === 0 ? <Vazio titulo="Nenhum token emitido" /> : (
            <table className="tabela">
              <thead>
                <tr><th>Identificador</th><th>Maquina</th><th>Chave</th><th>Motivo</th><th>Situacao</th></tr>
              </thead>
              <tbody>
                {r.tokens.map((t) => {
                  const vivo = !t.revogado_em && new Date(t.expira_em) > new Date();
                  return (
                    <tr key={t.jti}>
                      <td className="tabela__mono">{t.jti.slice(0, 12)}</td>
                      {/* O maquina_id sempre esteve na tabela e nunca aparecia:
                          via-se um jti sem saber de qual instalacao era. */}
                      <td className="pequeno">{t.hostname || <span className="apagado">--</span>}</td>
                      <td className="tabela__mono">{t.kid}</td>
                      <td className="suave pequeno">{t.motivo_emissao}</td>
                      <td>
                        {t.revogado_em ? <Selo tom="critico">revogado</Selo>
                          : vivo ? <Selo tom="ok">valido</Selo>
                          : <Selo tom="neutro">vencido</Selo>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Cartao>
      </div>

      <Cartao titulo="Historico desta licenca">
        {r.auditoria.length === 0 ? <Vazio titulo="Sem registros" /> : (
          <table className="tabela">
            <thead><tr><th>Quando</th><th>Quem</th><th>Acao</th></tr></thead>
            <tbody>
              {r.auditoria.map((a) => (
                <tr key={a.id}>
                  <td className="suave pequeno">{dataHora(a.ocorrido_em)}</td>
                  <td>{a.ator}</td>
                  <td className="tabela__mono">{a.acao}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Cartao>
    </>
  );
}
